import { useEffect, useState } from "react";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { format, subDays } from "date-fns";

interface MedicationLogsFilterForm {
  date_from?: string;
  date_to?: string;
}

interface StoredFilterState {
  quickFilterDays?: number; // Track if using quick filter (7 or 30)
  customFilters?: MedicationLogsFilterForm; // Store custom date range
}

const STORAGE_KEY = "medication_logs_filter_state";

export function useMedicationLogsFilterStorage(
  onFiltersLoaded: (filters: MedicationLogsFilterForm) => void,
) {
  const [activeQuickFilter, setActiveQuickFilter] = useState<
    number | undefined
  >();

  // Load filters from storage on mount
  useEffect(() => {
    loadFiltersFromStorage();
  }, []);

  const calculateQuickFilterDates = (
    days: number,
  ): MedicationLogsFilterForm => {
    const today = new Date();
    const startDate = subDays(today, days);
    return {
      date_from: format(startDate, "yyyy-MM-dd"),
      date_to: format(today, "yyyy-MM-dd"),
    };
  };

  const loadFiltersFromStorage = async () => {
    try {
      const stored = await AsyncStorage.getItem(STORAGE_KEY);
      if (stored) {
        const state: StoredFilterState = JSON.parse(stored);

        // If quick filter is active, recalculate dates based on current date
        if (state.quickFilterDays) {
          const filters = calculateQuickFilterDates(state.quickFilterDays);
          setActiveQuickFilter(state.quickFilterDays);
          onFiltersLoaded(filters);
        } else if (state.customFilters) {
          // Use stored custom filters
          onFiltersLoaded(state.customFilters);
        }
      }
    } catch (e) {
      console.error("Error loading filters:", e);
    }
  };

  const saveFiltersToStorage = async (state: StoredFilterState) => {
    try {
      await AsyncStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (e) {
      console.error("Error saving filters:", e);
    }
  };

  const saveQuickFilter = async (days: number) => {
    setActiveQuickFilter(days);
    await saveFiltersToStorage({ quickFilterDays: days });
  };

  const saveCustomFilters = async (filters: MedicationLogsFilterForm) => {
    setActiveQuickFilter(undefined);
    await saveFiltersToStorage({ customFilters: filters });
  };

  const clearFilters = async () => {
    setActiveQuickFilter(undefined);
    await AsyncStorage.removeItem(STORAGE_KEY);
  };

  const checkIfQuickFilterActive = (
    currentFilters: MedicationLogsFilterForm,
  ): boolean => {
    if (!activeQuickFilter) return false;

    const expectedFilters = calculateQuickFilterDates(activeQuickFilter);
    return (
      currentFilters.date_from === expectedFilters.date_from &&
      currentFilters.date_to === expectedFilters.date_to
    );
  };

  return {
    activeQuickFilter,
    setActiveQuickFilter,
    calculateQuickFilterDates,
    saveQuickFilter,
    saveCustomFilters,
    clearFilters,
    checkIfQuickFilterActive,
  };
}

export type { MedicationLogsFilterForm, StoredFilterState };
